import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { XIcon, FileTextIcon, AlertCircleIcon, CheckCircleIcon, ShieldIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
export const TeacherSignUpLayout = () => {
  const navigate = useNavigate();
  const {
    registerTeacher,
    currentUser
  } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    location: ''
  });
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [signupComplete, setSignupComplete] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const [showFirebaseRulesHelp, setShowFirebaseRulesHelp] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: 'bg-gray-300'
  });
  const [registrationAttempted, setRegistrationAttempted] = useState(false);
  useEffect(() => {
    if (currentUser) {
      if (currentUser.emailVerified) {
        navigate('/teacher-dashboard');
      } else {
        navigate('/teacher-email-verification');
      }
    }
  }, [currentUser, navigate]);
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(formData.password));
  }, [formData.password]);
  useEffect(() => {
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  }, [formData.password, formData.confirmPassword]);
  const calculatePasswordStrength = (password: string) => {
    if (!password || password.length < 1) {
      return {
        score: 0,
        label: 'Too Short',
        color: 'bg-gray-300'
      };
    }
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password)) score += 0.5;
    if (/[A-Z]/.test(password)) score += 0.5;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password)) score += 1;
    score = Math.min(4, score);
    const strengthMap = [{
      score: 0,
      label: 'Too Short',
      color: 'bg-gray-300'
    }, {
      score: 1,
      label: 'Weak',
      color: 'bg-red-500'
    }, {
      score: 2,
      label: 'Fair',
      color: 'bg-orange-500'
    }, {
      score: 3,
      label: 'Good',
      color: 'bg-yellow-500'
    }, {
      score: 4,
      label: 'Strong',
      color: 'bg-green-500'
    }];
    return strengthMap[Math.floor(score)];
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      id,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    setErrorMessage('');
  };
  useEffect(() => {
    if (!signupComplete) return;
    const countdownInterval = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          console.log('Redirecting to email verification page...');
          navigate('/teacher-email-verification');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, [signupComplete, navigate]);
  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setErrorMessage('Please enter your first name');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return false;
    }
    if (!formData.location.trim()) {
      setErrorMessage('Please enter your location');
      return false;
    }
    if (!verifyChecked) {
      setErrorMessage('Please verify the accuracy of your information');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (registrationAttempted || isSubmitting) {
      console.log('[Signup] Registration already in progress or completed');
      return;
    }
    setErrorMessage('');
    setSuccessMessage('');
    setShowFirebaseRulesHelp(false);
    if (!validateForm()) return;
    setIsSubmitting(true);
    setRegistrationAttempted(true);
    try {
      console.log('[Signup] Starting teacher registration process...');
      const displayName = `${formData.firstName} ${formData.lastName}`.trim();
      const locationParts = formData.location.split(',');
      const city = locationParts[0]?.trim() || '';
      const state = locationParts.length > 1 ? locationParts[1].trim() : '';
      const result = await registerTeacher(formData.email.trim(), formData.password, formData.firstName.trim(), formData.lastName.trim(), formData.location.trim(), city, state);
      console.log('[Signup] Teacher registration successful!', result);
      setIsSubmitting(false);
      setSuccessMessage('Your teacher account has been created! A verification email has been sent to your inbox.');
      setSignupComplete(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        location: ''
      });
      setVerifyChecked(false);
      navigate('/teacher-email-verification');
    } catch (error: any) {
      console.error('[Signup] Signup error:', error);
      setRegistrationAttempted(false);
      if (error.code === 'auth/email-already-in-use') {
        setErrorMessage('This email is already registered. Please sign in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setErrorMessage('Password is too weak. Please use a stronger password.');
      } else if (error.message && error.message.includes('Firebase permissions error')) {
        setErrorMessage('Missing or insufficient permissions.');
        setShowFirebaseRulesHelp(true);
      } else {
        setErrorMessage(error.message || 'An error occurred. Please try again later.');
      }
      setIsSubmitting(false);
    }
  };
  return <div className="w-full" data-id="element-11395">
      <section className="pt-32 pb-16 bg-navy-900 relative overflow-hidden" data-id="element-11396">
        <div className="absolute inset-0 z-0" data-id="element-11397">
          <div className="absolute inset-0 bg-navy-900/70 z-10" data-id="element-11398"></div>
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url('https://uploadthingy.s3.us-west-1.amazonaws.com/s8ktVYbcqPGNW3DKAXWQDj/Piano_teacher_3.jpg')`
        }} data-id="element-11399"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-11400">
          <div className="text-center" data-id="element-11401">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6" data-id="element-11402">
              Start Teaching on{' '}
              <span className="text-deep-pink" data-id="element-11403">MaestroConnect</span>
            </h1>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto" data-id="element-11404">
              Join a growing network of qualified music teachers and connect
              with students in your area or online.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-navy-900/5" data-id="element-11405">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-11406">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-8" data-id="element-11407">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-6 text-center" data-id="element-11408">
              Create Your Teacher Profile
            </h2>

            {successMessage && <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start" data-id="element-11409">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-11410" />
                <div data-id="element-11411">
                  <span data-id="element-11412">{successMessage}</span>
                  {signupComplete && <p className="text-sm mt-1" data-id="element-11413">
                      Redirecting to complete your profile in{' '}
                      {redirectCountdown} seconds...
                    </p>}
                </div>
              </div>}

            {errorMessage && <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start" data-id="element-11414">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-11415" />
                <span data-id="element-11416">{errorMessage}</span>
              </div>}

            {showFirebaseRulesHelp && <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg" data-id="element-11417">
                <h3 className="font-medium mb-2" data-id="element-11418">
                  Firebase Security Rules Issue
                </h3>
                <p className="mb-2" data-id="element-11419">
                  Your Firebase security rules need to be updated to allow
                  document creation. Update your Firestore rules to:
                </p>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto mb-2" data-id="element-11420">
                  {`rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /users/{userId} {
      allow read: if request.auth != null && request.auth.uid == userId;
      allow create: if request.auth != null && request.auth.uid == userId;
      allow update: if request.auth != null && request.auth.uid == userId;
    }
    match /teachers/{teacherId} {
      allow read: if request.auth != null && request.auth.uid == teacherId;
      allow create: if request.auth != null && request.auth.uid == teacherId;
      allow update: if request.auth != null && request.auth.uid == teacherId;
      match /calendar/{eventId} {
        allow read, write: if request.auth != null && request.auth.uid == teacherId;
      }
    }
    match /students/{studentId} {
      allow read: if request.auth != null && request.auth.uid == studentId;
      allow create: if request.auth != null && request.auth.uid == studentId;
      allow update: if request.auth != null && request.auth.uid == studentId;
    }
  }
}`}
                </pre>
                <p className="text-sm" data-id="element-11421">
                  Please update these rules in your Firebase Console under
                  Firestore Database &gt; Rules.
                </p>
              </div>}

            {!signupComplete && <form onSubmit={handleSubmit} data-id="element-11422">
                <div className="space-y-6" data-id="element-11423">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11424">
                    <div data-id="element-11425">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11426">
                        First Name <span className="text-red-500" data-id="element-11427">*</span>
                      </label>
                      <input type="text" id="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Your first name" required disabled={isSubmitting} data-id="element-11428" />
                    </div>
                    <div data-id="element-11429">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11430">
                        Last Name
                      </label>
                      <input type="text" id="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Your last name" disabled={isSubmitting} data-id="element-11431" />
                    </div>
                  </div>

                  <div data-id="element-11432">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11433">
                      Email Address <span className="text-red-500" data-id="element-11434">*</span>
                    </label>
                    <input type="email" id="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="you@example.com" required disabled={isSubmitting} data-id="element-11435" />
                  </div>

                  <div data-id="element-11436">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11437">
                      Password <span className="text-red-500" data-id="element-11438">*</span>
                    </label>
                    <div className="relative" data-id="element-11439">
                      <input type={passwordVisible ? 'text' : 'password'} id="password" value={formData.password} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Create a secure password (min. 8 characters)" required disabled={isSubmitting} data-id="element-11440" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setPasswordVisible(!passwordVisible)} disabled={isSubmitting} data-id="element-11441">
                        {passwordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-11442" /> : <EyeIcon className="h-5 w-5" data-id="element-11443" />}
                      </button>
                    </div>
                    <div className="mt-2" data-id="element-11444">
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-11445">
                        <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{
                      width: `${passwordStrength.score / 4 * 100}%`
                    }} data-id="element-11446"></div>
                      </div>
                      <div className="mt-1 flex justify-between items-center" data-id="element-11447">
                        <span className={`text-xs font-medium ${passwordStrength.score <= 1 ? 'text-red-500' : passwordStrength.score === 2 ? 'text-orange-500' : passwordStrength.score === 3 ? 'text-yellow-500' : 'text-green-500'}`} data-id="element-11448">
                          {passwordStrength.label}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-11449">
                          Use 8+ characters, include a number and symbol
                        </span>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-11450">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11451">
                      Confirm Password <span className="text-red-500" data-id="element-11452">*</span>
                    </label>
                    <div className="relative" data-id="element-11453">
                      <input type={confirmPasswordVisible ? 'text' : 'password'} id="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900 ${passwordError ? 'border-red-500' : 'border-gray-300'}`} placeholder="Confirm your password" required disabled={isSubmitting} data-id="element-11454" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} disabled={isSubmitting} data-id="element-11455">
                        {confirmPasswordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-11456" /> : <EyeIcon className="h-5 w-5" data-id="element-11457" />}
                      </button>
                    </div>
                    {passwordError && <p className="mt-1 text-sm text-red-500" data-id="element-11458">
                        {passwordError}
                      </p>}
                  </div>

                  <div data-id="element-11459">
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11460">
                      Location (City, State){' '}
                      <span className="text-red-500" data-id="element-11461">*</span>
                    </label>
                    <input type="text" id="location" value={formData.location} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="e.g. Los Angeles, CA" required disabled={isSubmitting} data-id="element-11462" />
                  </div>

                  <div className="flex items-start" data-id="element-11463">
                    <div className="flex items-center h-5" data-id="element-11464">
                      <input id="verify" name="verify" type="checkbox" checked={verifyChecked} onChange={() => setVerifyChecked(!verifyChecked)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 rounded" required disabled={isSubmitting} data-id="element-11465" />
                    </div>
                    <div className="ml-3 text-sm" data-id="element-11466">
                      <label htmlFor="verify" className="font-medium text-gray-700" data-id="element-11467">
                        I verify the accuracy of my information and agree to the{' '}
                        <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-700" data-id="element-11468">
                          Terms of Service
                        </Link>
                      </label>
                    </div>
                  </div>

                  <button type="submit" disabled={isSubmitting || !!passwordError || registrationAttempted} className={`w-full ${isSubmitting || passwordError || registrationAttempted ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white font-medium py-3 px-4 rounded-lg transition-colors flex justify-center items-center`} data-id="element-11469">
                    {isSubmitting ? <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-11470">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-11471"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-11472"></path>
                        </svg>
                        Creating Account...
                      </> : 'Start Free'}
                  </button>

                  <p className="text-sm text-gray-500 text-center mt-4" data-id="element-11473">
                    You won't be charged during your 14-day trial. You can
                    cancel anytime.
                  </p>

                  <p className="text-sm text-gray-500 text-center mt-4" data-id="element-11474">
                    Already have an account?{' '}
                    <Link to="/teacher-login" className="text-blue-600 hover:text-blue-700" data-id="element-11475">
                      Sign In
                    </Link>
                  </p>
                </div>
              </form>}

            {signupComplete && <div className="text-center py-8" data-id="element-11476">
                <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center" data-id="element-11477">
                  <CheckCircleIcon className="h-12 w-12 text-green-500" data-id="element-11478" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-11479">
                  Account Created Successfully!
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-11480">
                  A verification email has been sent to your inbox. We're
                  redirecting you in {redirectCountdown} seconds.
                </p>
                <button onClick={() => navigate('/teacher-email-verification')} className="bg-deep-pink hover:bg-burgundy text-white font-medium py-2 px-6 rounded-lg transition-colors" data-id="element-11481">
                  Verify Your Email
                </button>
              </div>}
          </div>
        </div>
      </section>
    </div>;
};